import type {AppNotification} from '@/state/notificationAtoms';

/**
 * A mutation the server answered but did not carry out: no data at all, or
 * errors where every root field came back null (nullable mutation fields
 * resolve to null when the resolver throws, so a rejected write still
 * arrives as a "successful" response with a data object in it).
 *
 * ADDED 2026-10-08 (Duane, relaying a tester): a Daily Report for the
 * oldest day of the 7-day window sometimes saved the tank gauge but not
 * the report, only at night. useOfflineMutation's `errorPolicy: 'all'`
 * plus a bare "does data exist" check counted that rejection as success —
 * drawer closed, no message, nothing saved. This error carries the raw
 * server text in `details` for a "Show details" toast; the `message` is
 * only used for logs/tests, since server text is rarely user-facing.
 */
export class MutationRejectedError extends Error {
   readonly details: string;
   constructor(message: string, details: string) {
      super(message);
      this.name = 'MutationRejectedError';
      this.details = details;
   }
}

interface ErrorLike {
   message?: unknown;
   path?: unknown;
   extensions?: unknown;
}

function describeOne(e: unknown): string {
   if (e == null) return 'Unknown error';
   if (typeof e === 'string') return e;
   const {message, path, extensions} = e as ErrorLike;
   const parts = [typeof message === 'string' && message ? message : 'Unknown error'];
   if (Array.isArray(path) && path.length > 0) parts.push(`path: ${path.join('.')}`);
   const code = (extensions as {code?: unknown} | null | undefined)?.code;
   if (typeof code === 'string' && code) parts.push(`code: ${code}`);
   return parts.join(' — ');
}

/** Returns a MutationRejectedError when the response is a rejection, else null. */
export function mutationFailure(
   data: Record<string, unknown> | null | undefined,
   errors: readonly unknown[] | undefined,
   label?: string,
): MutationRejectedError | null {
   const hasErrors = (errors?.length ?? 0) > 0;
   const allRootsNull = data != null && Object.values(data).every((v) => v == null);
   if (data != null && !(hasErrors && allRootsNull)) return null;

   const lines = hasErrors ? (errors ?? []).map(describeOne) : ['The server returned no data.'];
   const details = (label ? [`${label}`, ...lines] : lines).join('\n');
   return new MutationRejectedError(hasErrors ? describeFirst(errors) : 'Mutation returned no data', details);
}

function describeFirst(errors: readonly unknown[] | undefined): string {
   const first = errors?.[0] as ErrorLike | undefined;
   return typeof first?.message === 'string' && first.message ? first.message : 'Mutation rejected';
}

// Pulls human-readable messages out of whatever shape a server error body
// took: a bare array of {message} (what the backend actually sends for a
// rejected write, e.g. [{"message": "Entry date cannot be more than 8 days
// in the past."}]), a standard {errors: [...]} envelope, or either of those
// as a raw JSON string.
function messagesFromBody(body: unknown): string[] {
   if (body == null) return [];
   if (typeof body === 'string') {
      const text = body.trim();
      if (!text) return [];
      try {
         const parsed: unknown = JSON.parse(text);
         if (typeof parsed === 'object' && parsed !== null) return messagesFromBody(parsed);
      } catch {
         // Not JSON — fall through to treating it as plain text.
      }
      return [text];
   }
   if (Array.isArray(body)) return body.flatMap(messagesFromBody);
   if (typeof body === 'object') {
      const {message, errors} = body as {message?: unknown; errors?: unknown};
      if (Array.isArray(errors) && errors.length > 0) return messagesFromBody(errors);
      if (typeof message === 'string' && message) return [message];
   }
   return [];
}

/**
 * The server's own error messages for a failed request, if it sent any.
 *
 * ADDED 2026-10-30 (Duane, relaying a tester): saving a Production entry
 * older than 7 days on WellEditPage failed with
 * [{"message": "Entry date cannot be more than 8 days in the past."}] and
 * the user never saw it. That body is a bare array (no {errors: [...]}
 * wrapper), so Apollo rejects with a generic "Response not successful"
 * ApolloError and tucks the real text in networkError.result — which
 * errorDetails() used to ignore, since it only read err.message.
 */
export function serverErrorMessages(err: unknown): string[] {
   if (err instanceof MutationRejectedError) return [err.message];
   if (typeof err !== 'object' || err === null) return [];
   const {graphQLErrors, networkError} = err as {graphQLErrors?: unknown; networkError?: unknown};
   const fromGraphQL = messagesFromBody(graphQLErrors);
   if (fromGraphQL.length > 0) return fromGraphQL;
   if (typeof networkError === 'object' && networkError !== null) {
      const {result, bodyText} = networkError as {result?: unknown; bodyText?: unknown};
      const fromResult = messagesFromBody(result);
      if (fromResult.length > 0) return fromResult;
      return messagesFromBody(bodyText);
   }
   return [];
}

/** Raw technical text for the toast's expandable details, for any thrown value. */
export function errorDetails(err: unknown): string {
   if (err instanceof MutationRejectedError) return err.details;
   const serverMessages = serverErrorMessages(err);
   if (serverMessages.length > 0) return serverMessages.join('\n');
   if (err instanceof Error) return err.message || err.name;
   return describeOne(err);
}

/**
 * User-facing toast for a failed save: a plain message up front, the
 * server's own text behind a "Show details" button (see
 * NotificationSnackbar) — server messages are mostly not meant for pumpers.
 */
export function saveFailureNotification(message: string, err: unknown): Omit<AppNotification, 'id'> {
   return {message, severity: 'warning', details: errorDetails(err)};
}
